import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, PenLine, RotateCcw } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast/useToast';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { safetyApi, type QuizResult } from '../api/safetyApi';
import { useCompleteMutation, useSafetyCourse } from '../hooks';
import { AttestationFooter, PnTag, SafetyLoadError } from '../components/SafetyBits';
import styles from './TakeCoursePage.module.css';

type Stage = 'lesson' | 'quiz' | 'sign' | 'done';

export function TakeCoursePage() {
  const { assignmentId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: course, isLoading, isError, locationId, refetch } = useSafetyCourse(assignmentId);
  const complete = useCompleteMutation(assignmentId);

  const [stage, setStage] = useState<Stage>('lesson');
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);
  const [signerName, setSignerName] = useState('');

  if (isError) {
    return (
      <div className={patterns.page}>
        <SafetyLoadError onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !course) {
    return (
      <div className={patterns.page}>
        <Skeleton height={80} />
        <Skeleton height={400} />
      </div>
    );
  }

  const allAnswered = course.quiz.every((item) => answers[item.id] !== undefined);

  const handleCheckQuiz = async () => {
    setChecking(true);
    try {
      const result = await safetyApi.submitQuiz(
        assignmentId,
        locationId,
        Object.entries(answers).map(([itemId, answerIndex]) => ({ itemId, answerIndex })),
      );
      setQuizResult(result);
      if (result.passed) {
        setSignerName(course.staff.name);
        setStage('sign');
      }
    } catch {
      toast.error('Could not score the quiz — try again');
    } finally {
      setChecking(false);
    }
  };

  const handleRetake = async () => {
    setAnswers({});
    setQuizResult(null);
    await refetch(); // reshuffles the presented items
    setStage('lesson');
  };

  const handleSign = async () => {
    try {
      const res = await complete.mutateAsync({
        answers: Object.entries(answers).map(([itemId, answerIndex]) => ({
          itemId,
          answerIndex,
        })),
        signerName,
      });
      setQuizResult(res.result);
      setStage('done');
    } catch {
      toast.error('Could not record the completion — try again');
    }
  };

  return (
    <div className={`${patterns.page} ${styles.player}`}>
      <button className={styles.backLink} onClick={() => navigate('/safety/training')}>
        <ArrowLeft size={13} aria-hidden /> Staff Training
      </button>

      <div className={styles.courseHead}>
        <h1 className={styles.courseTitle}>
          {course.course.title} <PnTag action="Deliver" />
        </h1>
        <p className={styles.courseMeta}>
          {course.staff.name} · {course.staff.roleLabel} · {course.topics.length} topics · quiz pass
          ≥ {Math.round(course.passThreshold * 100)}% · v{course.course.version}
        </p>
      </div>

      {course.assignment.completed && stage === 'lesson' && (
        <div className={styles.completedNote}>
          This training is already completed and e-signed — you can review the material below.
        </div>
      )}

      {stage === 'lesson' && (
        <>
          {course.topics.map((topic, i) => (
            <section key={topic.id} className={patterns.panel}>
              <div className={styles.topicHead}>
                <span className={styles.topicNum}>{i + 1}</span>
                <div>
                  <h2 className={styles.topicTitle}>{topic.title}</h2>
                  <span className={styles.topicSource}>{topic.source}</span>
                </div>
              </div>
              <div className={styles.sections}>
                {topic.sections.map((section) => (
                  <div key={section.heading} className={styles.section}>
                    <h3 className={styles.sectionHeading}>{section.heading}</h3>
                    <p className={styles.sectionBody}>{section.body}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {!course.assignment.completed && (
            <div className={styles.stageActions}>
              <Button
                variant="primary"
                rightIcon={<ArrowRight size={14} aria-hidden />}
                onClick={() => setStage('quiz')}
              >
                Continue to quiz
              </Button>
            </div>
          )}
        </>
      )}

      {stage === 'quiz' && (
        <>
          <div className={patterns.panel}>
            <h2 className={styles.quizTitle}>Knowledge check</h2>
            <p className={styles.quizSub}>
              {course.quiz.length} questions · pass ≥ {Math.round(course.passThreshold * 100)}% ·
              answers are scored against the vetted key, never by AI.
            </p>
            <div className={styles.quizList}>
              {course.quiz.map((item, qi) => (
                <fieldset key={item.id} className={styles.quizItem}>
                  <legend className={styles.quizQuestion}>
                    {qi + 1}. {item.q}
                  </legend>
                  <div className={styles.quizOptions}>
                    {item.options.map((option, oi) => (
                      <label key={oi} className={styles.quizOption}>
                        <input
                          type="radio"
                          name={item.id}
                          checked={answers[item.id] === oi}
                          onChange={() => setAnswers((a) => ({ ...a, [item.id]: oi }))}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
          </div>

          {quizResult && !quizResult.passed && (
            <div className={styles.failNote}>
              You scored {Math.round(quizResult.score * 100)}% ({quizResult.correct} of{' '}
              {quizResult.total}) — below the {Math.round(course.passThreshold * 100)}% pass mark.
              Review the material and try again; a fresh set of questions will be drawn.
            </div>
          )}

          <div className={styles.stageActions}>
            <Button variant="secondary" onClick={() => setStage('lesson')}>
              Back to material
            </Button>
            {quizResult && !quizResult.passed ? (
              <Button
                variant="primary"
                leftIcon={<RotateCcw size={14} aria-hidden />}
                onClick={handleRetake}
              >
                Review &amp; retake
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleCheckQuiz}
                disabled={!allAnswered}
                loading={checking}
              >
                Submit answers
              </Button>
            )}
          </div>
        </>
      )}

      {stage === 'sign' && quizResult && (
        <div className={patterns.panel}>
          <div className={styles.signHead}>
            <CheckCircle2 size={20} aria-hidden className={styles.passIcon} />
            <div>
              <h2 className={styles.quizTitle}>Passed — {Math.round(quizResult.score * 100)}%</h2>
              <p className={styles.quizSub}>
                {quizResult.correct} of {quizResult.total} correct. One signature covers all{' '}
                {course.topics.length} topics.
              </p>
            </div>
          </div>

          <div className={styles.topicsReviewed}>
            <span className={patterns.fieldLabel}>Topics reviewed</span>
            <span className={styles.topicsList}>
              {course.topics.map((t) => t.shortTitle).join(' · ')}
            </span>
          </div>

          <label className={styles.ackRow}>
            <input
              type="checkbox"
              checked={ackChecked}
              onChange={(e) => setAckChecked(e.target.checked)}
            />
            <span>{course.ackText}</span>
          </label>

          <div className={styles.signRow}>
            <Input
              label="Type your full name to sign"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder={course.staff.name}
              fullWidth
            />
          </div>

          <div className={styles.stageActions}>
            <Button
              variant="primary"
              leftIcon={<PenLine size={14} aria-hidden />}
              onClick={handleSign}
              disabled={!ackChecked || signerName.trim().length === 0}
              loading={complete.isPending}
            >
              Sign &amp; submit
            </Button>
          </div>
        </div>
      )}

      {stage === 'done' && quizResult && (
        <div className={`${patterns.panel} ${styles.doneCard}`}>
          <CheckCircle2 size={28} aria-hidden className={styles.passIcon} />
          <h2 className={styles.quizTitle}>Training recorded</h2>
          <p className={styles.quizSub}>
            {course.staff.name} · {Math.round(quizResult.score * 100)}% · e-signed. The completion
            record itemizes every topic and is retained for 3 years (HIPAA records 6 years).
          </p>
          <div className={styles.stageActions}>
            <Button variant="secondary" onClick={() => navigate('/safety/records')}>
              View records
            </Button>
            <Button variant="primary" onClick={() => navigate('/safety/training')}>
              Back to Staff Training
            </Button>
          </div>
        </div>
      )}

      <AttestationFooter text={course.attestation} />
    </div>
  );
}
